import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Search } from 'lucide-react';

import { listCommsMembers } from '@/api/comms/members.api';

/**
 * Org-transparent member picker over `/api/comms/members` — shared by the
 * shout-out recipient picker (single) and the chat participant picker
 * (multi, Agent E). Self-contained: owns its own search + selection
 * state and reports the current selection through `onSelect`.
 *
 * Contract (kept stable for the shared consumers):
 *   - `multi`   — checkbox multi-select (default: single-select).
 *   - `exclude` — membershipIds to hide (e.g. self).
 *   - `onSelect(ids)` — fires on every selection change with the full
 *     list of selected membershipIds.
 */
export interface MemberPickerProps {
  multi?: boolean;
  exclude?: string[];
  onSelect: (ids: string[]) => void;
}

export function MemberPicker({ multi = false, exclude, onSelect }: MemberPickerProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const membersQuery = useQuery({ queryKey: ['comms', 'members'], queryFn: listCommsMembers });

  const excludeSet = useMemo(() => new Set(exclude ?? []), [exclude]);
  const members = useMemo(() => {
    const all = (membersQuery.data ?? []).filter((m) => !excludeSet.has(m.membershipId));
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((m) => `${m.firstName} ${m.lastName}`.toLowerCase().includes(q));
  }, [membersQuery.data, excludeSet, query]);

  function toggle(id: string): void {
    let next: string[];
    if (multi) {
      next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    } else {
      next = selected.includes(id) ? [] : [id];
    }
    setSelected(next);
    onSelect(next);
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-kw-muted" />
        <input
          type="search"
          aria-label="Search members"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          placeholder="Search people…"
          className="w-full rounded-kw-md border border-kw-border bg-white py-2 pl-8 pr-3 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
        />
      </div>

      <div className="max-h-56 overflow-y-auto rounded-kw-md border border-kw-border">
        {membersQuery.isLoading ? (
          <p className="px-3 py-4 text-center text-[13px] text-kw-muted">Loading people…</p>
        ) : members.length === 0 ? (
          <p className="px-3 py-4 text-center text-[13px] text-kw-muted">No matching people.</p>
        ) : (
          <ul className="divide-y divide-kw-border">
            {members.map((m) => {
              const isSelected = selected.includes(m.membershipId);
              const name = `${m.firstName} ${m.lastName}`.trim();
              return (
                <li key={m.membershipId}>
                  <button
                    type="button"
                    aria-pressed={isSelected}
                    aria-label={`Select ${name}`}
                    onClick={() => {
                      toggle(m.membershipId);
                    }}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13px] transition-colors ${
                      isSelected
                        ? 'bg-kw-cerulean-light text-kw-cerulean-dark'
                        : 'text-kw-shark hover:bg-kw-bg'
                    }`}
                  >
                    <span className="min-w-0 flex-1 truncate">{name}</span>
                    {isSelected ? <Check className="size-4 shrink-0 text-kw-cerulean" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
